import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { getMe, logoutUser } from "../Api/apiService";
import { getAuthToken, setAuthToken, removeAuthToken } from "../Utils/storage";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const data = await getMe();
      if (data && data.success && data.user) {
        setUser(data.user);
      } else {
        setUser(null);
        removeAuthToken();
      }
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      setUser(null);
      removeAuthToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      if (isMounted) {
        await fetchUser();
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [fetchUser]);

  const login = useCallback(
    async (accessToken, userData) => {
      if (accessToken && userData) {
        setAuthToken(accessToken);
        setUser(userData);
        setLoading(true);
        await fetchUser();
      }
    },
    [fetchUser],
  );

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (err) {
      console.error("Backend logout failed:", err);
    } finally {
      removeAuthToken();
      setUser(null);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
      refreshUser: fetchUser,
    }),
    [user, loading, login, logout, fetchUser],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
